import moment from 'moment';

import {
  IServiceAuditorCounts,
  IServiceAuditorDraftReadCoverage,
  IServiceAuditorFinding,
  IServiceAuditorReport,
  IServiceAuditorSummary,
  ServiceAuditorDisplayStatus,
  ServiceAuditorFindingCategory,
} from '@commenda/api/agent-platform/service-auditor/types';
import { pluralize } from '@commenda/utils';

import { IServiceAuditBadgeConfig } from './ServiceAuditFindingBadgeHelper';
import { ServiceAuditFindingHelper } from './ServiceAuditFindingHelper';
import {
  IServiceAuditPreviousOutcomeItem,
  ServiceAuditPreviousRoundHelper,
  TServiceAuditPreviousOutcomeListKey,
} from './ServiceAuditPreviousRoundHelper';

export enum ServiceAuditReportSectionKey {
  PENALTY_RISKS = 'PENALTY_RISKS',
  OTHER_FINDINGS = 'OTHER_FINDINGS',
  FIXED_SINCE_LAST_DRAFT = 'FIXED_SINCE_LAST_DRAFT',
  NOT_A_MISTAKE = 'NOT_A_MISTAKE',
  UNSETTLED_FROM_LAST_ROUND = 'UNSETTLED_FROM_LAST_ROUND',
  INTAKE_GAPS = 'INTAKE_GAPS',
  CHECKS_PASSED = 'CHECKS_PASSED',
}

export interface IServiceAuditReportSectionConfig
  extends IServiceAuditBadgeConfig {
  key: ServiceAuditReportSectionKey;
  subtitle: string;
  isOpenByDefault: boolean;
  // Opens the section by default when it lists a last-round finding marked Real, so the reviewer looks.
  isOpenedByPreviouslyReal: boolean;
  // The finding category the section lists from report.findings; null for intake gaps and checks passed.
  findingCategory: ServiceAuditorFindingCategory | null;
  // Label of the summary strip count badge; null when the section has none.
  countBadgeLabel: string | null;
  // The list of report.previousRound the section shows; null for every other section.
  previousOutcomeList: TServiceAuditPreviousOutcomeListKey | null;
}

export interface IServiceAuditReportSectionView {
  config: IServiceAuditReportSectionConfig;
  findings: IServiceAuditorFinding[];
  checksPassed: string[];
  previousOutcomes: IServiceAuditPreviousOutcomeItem[];
  itemCount: number;
  // Null for checks passed and last-round lists, which take no feedback.
  progressLabel: string | null;
  isOpenByDefault: boolean;
}

export interface IServiceAuditCountBadge extends IServiceAuditBadgeConfig {
  key: ServiceAuditReportSectionKey;
}

export interface IServiceAuditRunOption {
  runId: string;
  label: string;
}

export class ServiceAuditReportHelper {
  static readonly AUDIT_DATE_FORMAT = 'DD MMM, YYYY, h:mm A';

  static readonly SHEET_TITLE = 'Audit report';

  static readonly OPEN_REPORT_LABEL = 'Open report';

  static readonly AUDIT_PICKER_LABEL = 'Audit';

  static readonly DETAILS_AND_PROOF_LABEL = 'Details and proof';

  static readonly EMPTY_STATE_TITLE = 'No proven findings in this draft';

  static readonly DRAFT_TRUNCATED_WARNING =
    'The auditor did not read the whole draft. Check the pages it skipped by hand before approving.';

  static readonly DRAFT_READ_IN_FULL_LINE = 'Read the whole file';

  // Findings split only into penalty risks and the rest; intake gaps and passed checks keep their sections.
  static readonly SECTIONS: ReadonlyArray<IServiceAuditReportSectionConfig> = [
    {
      key: ServiceAuditReportSectionKey.PENALTY_RISKS,
      label: 'Penalty risks',
      findingCategory: ServiceAuditorFindingCategory.PENALTY_RISK,
      countBadgeLabel: 'Penalty risks',
      variant: 'error',
      subtitle:
        'Forms or schedules this filing requires that the draft leaves out or does not file.',
      isOpenByDefault: true,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: null,
    },
    {
      key: ServiceAuditReportSectionKey.OTHER_FINDINGS,
      label: 'Other findings',
      findingCategory: ServiceAuditorFindingCategory.OTHER,
      countBadgeLabel: null,
      variant: 'gray',
      subtitle:
        'Wrong, blank or inconsistent values in the draft, most severe first.',
      isOpenByDefault: true,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: null,
    },
    {
      key: ServiceAuditReportSectionKey.FIXED_SINCE_LAST_DRAFT,
      label: 'Fixed since last draft',
      findingCategory: null,
      countBadgeLabel: null,
      variant: 'success',
      subtitle:
        "Last round's findings that this draft fixes, with the text that shows the fix.",
      isOpenByDefault: false,
      isOpenedByPreviouslyReal: true,
      previousOutcomeList: 'fixed',
    },
    {
      key: ServiceAuditReportSectionKey.NOT_A_MISTAKE,
      label: 'Not a mistake, per this round',
      findingCategory: null,
      countBadgeLabel: null,
      variant: 'gray',
      subtitle:
        "Last round's findings the auditor now says are not mistakes, with its reason.",
      isOpenByDefault: false,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: 'notAMistake',
    },
    {
      key: ServiceAuditReportSectionKey.UNSETTLED_FROM_LAST_ROUND,
      label: "Couldn't confirm against last round",
      findingCategory: null,
      countBadgeLabel: null,
      variant: 'warning',
      subtitle:
        "Last round's findings this audit did not settle. Check them by hand before approving.",
      isOpenByDefault: true,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: 'unsettled',
    },
    {
      key: ServiceAuditReportSectionKey.INTAKE_GAPS,
      label: 'Intake gaps for Commenda',
      findingCategory: null,
      countBadgeLabel: 'Intake gaps',
      variant: 'blue',
      subtitle:
        'Information the final document needs that this workflow never collects. Fix the workflow, not the draft.',
      isOpenByDefault: true,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: null,
    },
    {
      key: ServiceAuditReportSectionKey.CHECKS_PASSED,
      label: 'Checks passed',
      findingCategory: null,
      countBadgeLabel: null,
      variant: 'success',
      subtitle: 'Areas the auditor checked and found consistent.',
      isOpenByDefault: false,
      isOpenedByPreviouslyReal: false,
      previousOutcomeList: null,
    },
  ];

  // Only finished audits have a report worth reading; newest first, as the list arrives.
  /** Only the newest audit takes feedback, whatever its status: a queued, running or failed audit of a newer draft closes the older reports. The list is newest first. */
  static isFeedbackOpenForRun(
    runId: string | null,
    audits: ReadonlyArray<IServiceAuditorSummary>
  ): boolean {
    return runId !== null && runId === (audits[0]?.runId ?? null);
  }

  static getCompletedAudits(
    audits: ReadonlyArray<IServiceAuditorSummary>
  ): IServiceAuditorSummary[] {
    return audits.filter(
      (audit) => audit.displayStatus === ServiceAuditorDisplayStatus.COMPLETED
    );
  }

  static getAuditOptions(
    audits: ReadonlyArray<IServiceAuditorSummary>
  ): IServiceAuditRunOption[] {
    return audits.map((audit) => ({
      runId: audit.runId,
      label: `${ServiceAuditReportHelper.formatAuditDate(audit.endedAt ?? audit.createdAt)}, ${pluralize(audit.findingCount, 'finding')}`,
    }));
  }

  static formatAuditDate(date: string): string {
    return moment(date).format(ServiceAuditReportHelper.AUDIT_DATE_FORMAT);
  }

  static getAuditedLine(report: Readonly<IServiceAuditorReport>): string {
    return `Audited ${ServiceAuditReportHelper.formatAuditDate(report.endedAt ?? report.createdAt)}`;
  }

  static getFileChangedAlert(report: Readonly<IServiceAuditorReport>): string {
    return `This file changed after the audit. The findings below are for the version audited on ${ServiceAuditReportHelper.formatAuditDate(report.endedAt ?? report.createdAt)}.`;
  }

  // Areas are listed by name, so a repeated name counts once.
  static getUniqueChecksPassed(
    report: Readonly<Pick<IServiceAuditorReport, 'checksPassed'>>
  ): string[] {
    return [...new Set(report.checksPassed)];
  }

  static getSummarySentence(
    counts: Readonly<IServiceAuditorCounts>,
    checksPassedCount: number
  ): string {
    const checks = pluralize(
      checksPassedCount,
      'check passed',
      'checks passed'
    );
    if (!ServiceAuditReportHelper.hasProvenItems(counts)) {
      return `No proven findings. ${checks}`;
    }
    const findings = pluralize(counts.findingCount, 'finding');
    const penaltyRisks = pluralize(counts.penaltyRiskCount, 'penalty risk');
    const intakeGaps = pluralize(counts.intakeGapCount, 'intake gap');
    return `${findings} (${penaltyRisks}), ${intakeGaps}, ${checks}`;
  }

  static getCountBadges(
    counts: Readonly<IServiceAuditorCounts>
  ): IServiceAuditCountBadge[] {
    const countByKey: Readonly<
      Partial<Record<ServiceAuditReportSectionKey, number>>
    > = {
      [ServiceAuditReportSectionKey.PENALTY_RISKS]: counts.penaltyRiskCount,
      [ServiceAuditReportSectionKey.INTAKE_GAPS]: counts.intakeGapCount,
    };
    return ServiceAuditReportHelper.SECTIONS.flatMap((config) => {
      const count = countByKey[config.key] ?? 0;
      if (!config.countBadgeLabel || count === 0) return [];
      return [
        {
          key: config.key,
          variant: config.variant,
          label: `${config.countBadgeLabel}: ${count}`,
        },
      ];
    });
  }

  static getDraftReadCoverageLine(
    coverage: Readonly<IServiceAuditorDraftReadCoverage> | null
  ): string | null {
    if (!coverage) return null;
    // A file without pages (Word, spreadsheet) read in full has no page count and is not truncated.
    if (coverage.totalPages === null) {
      return coverage.truncated
        ? `Read ${pluralize(coverage.pagesRead, 'page')}`
        : ServiceAuditReportHelper.DRAFT_READ_IN_FULL_LINE;
    }
    return `Read ${coverage.pagesRead} of ${pluralize(coverage.totalPages, 'page')}`;
  }

  static getDraftReadCoverageWarning(
    coverage: Readonly<IServiceAuditorDraftReadCoverage> | null
  ): string | null {
    return coverage?.truncated
      ? ServiceAuditReportHelper.DRAFT_TRUNCATED_WARNING
      : null;
  }

  static getFeedbackProgress(givenCount: number, totalCount: number): string {
    return `${givenCount} of ${totalCount} reviewed`;
  }

  // In SECTIONS order, empty sections left out; findings inside each by severity, then the backend's rank.
  static getReportSections(
    report: Readonly<IServiceAuditorReport>
  ): IServiceAuditReportSectionView[] {
    const uniqueChecksPassed =
      ServiceAuditReportHelper.getUniqueChecksPassed(report);
    return ServiceAuditReportHelper.SECTIONS.map(
      (config): IServiceAuditReportSectionView => {
        if (config.key === ServiceAuditReportSectionKey.CHECKS_PASSED) {
          return {
            config,
            findings: [],
            checksPassed: uniqueChecksPassed,
            previousOutcomes: [],
            itemCount: uniqueChecksPassed.length,
            progressLabel: null,
            isOpenByDefault: config.isOpenByDefault,
          };
        }
        if (config.previousOutcomeList) {
          const previousOutcomes =
            ServiceAuditPreviousRoundHelper.getOutcomeItems(
              report,
              config.previousOutcomeList
            );
          return {
            config,
            findings: [],
            checksPassed: [],
            previousOutcomes,
            itemCount: previousOutcomes.length,
            progressLabel: null,
            isOpenByDefault:
              config.isOpenByDefault ||
              (config.isOpenedByPreviouslyReal &&
                previousOutcomes.some((item) => item.isPreviouslyMarkedReal)),
          };
        }
        const findings = ServiceAuditFindingHelper.sortFindings(
          config.key === ServiceAuditReportSectionKey.INTAKE_GAPS
            ? report.intakeGaps
            : report.findings.filter(
                (finding) => finding.category === config.findingCategory
              )
        );
        return {
          config,
          findings,
          checksPassed: [],
          previousOutcomes: [],
          itemCount: findings.length,
          progressLabel: ServiceAuditReportHelper.getFeedbackProgress(
            ServiceAuditReportHelper.countWithFeedback(findings),
            findings.length
          ),
          isOpenByDefault: config.isOpenByDefault,
        };
      }
    ).filter((section) => section.itemCount > 0);
  }

  static getDefaultOpenSectionKeys(
    sections: ReadonlyArray<IServiceAuditReportSectionView>
  ): string[] {
    return sections
      .filter((section) => section.isOpenByDefault)
      .map((section) => section.config.key);
  }

  // Intake gaps count too, so a report with only intake gaps never shows the empty state.
  static hasProvenItems(counts: Readonly<IServiceAuditorCounts>): boolean {
    return counts.findingCount > 0 || counts.intakeGapCount > 0;
  }

  static getEmptyStateBody(checksPassedCount: number): string {
    return `The auditor checked ${pluralize(checksPassedCount, 'area')} and could not prove any mistake. Look over the passed list, then approve in the approval step.`;
  }

  // Only feedback saved on the finding itself counts; a last-round review hint never does.
  private static countWithFeedback(
    findings: ReadonlyArray<IServiceAuditorFinding>
  ): number {
    return findings.filter((finding) => finding.feedback !== null).length;
  }
}
